import axios from 'axios'

const util = require('../../util.js')

const default_filter = {
  organization: null,
  organizations: [],
  departments: [],
  isActive: Boolean(true),
  isSchool: Boolean(true),
  source: null,
  school: 'All',
}

const default_organization = {
  organization: {
    name: undefined,
    characteristic: undefined,
    organizationName: undefined,
    organizationNumber: undefined,
    organizationSource: undefined,
    sourceId: undefined,
    school: undefined,
    schoolOwner: undefined,
    active: undefined,
    externallyModified: undefined,
    numberOfPupils: undefined,
    numberOfPupilsOverride: undefined,
    departments: [],
  }
}

const default_department = {
  departmentId: undefined,
  name: undefined,
  organizationSource: undefined,
  organizationNumber: undefined,
}

const default_paging = {
  currentPage: 1,
  perPage: 25,
  perPageAlternatives: [25, 50, 100],
  totalRows: 0
}

const state = {
  organization: { ...default_organization },
  organizations: [],
  filter: { ...default_filter },
  loading: false,
  paging: { ...default_paging },
  createLoading: false,
  disableFilters: false,
  department: { ...default_department },
}

const mutations = {
  UPDATE_ORGANIZATIONS (state, payload) {
    state.organizations = payload.response
    state.paging.totalRows = payload.resultCount
  },
  UPDATE_FILTER (state, payload) {
    state.filter = payload
    state.organizations = []
  },
  UPDATE_ORGANIZATION (state, payload) {
    state.organization = payload
  },
  UPDATE_FORM_DISABLE_INDICATOR (state, payload) {
    state.loading = payload
  },
  UPDATE_CREATE_LOADING_INDICATOR (state, payload) {
    state.createLoading = payload
  },
  UPDATE_PAGING (state, payload) {
    state.paging = payload
  },
  UPDATE_ORGANIZATION_NUMBER (state, payload) {
    state.filter.organizationNumber = payload
  },
  UPDATE_SEARCH_FILTERS (state, payload) {
    state.disableFilters = payload
  },
  UPDATE_DEPARTMENTS (state, payload) {
    state.organization.departments = payload
  },
  UPDATE_DEPARTMENT (state, payload) {
    state.department = payload
    if (!state.organization)
      return
    if (!state.organization.departments)
      state.organization.departments = []
    const index = state.organization.departments
      .findIndex(({ departmentId }) => departmentId === payload.departmentId)
    if (index > -1) {
      state.organization.departments.splice(index, 1, payload)
    } else {
      state.organization.departments.push(payload)
    }
  },
  UPDATE_DEPARTMENT_USERS (state, payload) {
    if (state.department)
      state.department.users = payload
  },
  ADD_DEPARTMENT_USER (state, payload) {
    if (!state.department)
      return
    if (!state.department.users)
      state.department.users = []
    const index  = state.department.users
      .findIndex(({ userId }) => userId === payload.userId)
    if (index > -1) {
      state.department.users.splice(index, 1, payload)
    } else {
      state.department.users.push(payload)
    }
  },
  REMOVE_DEPARTMENT_USER (state, payload) {
    if (state.department)
      state.department.users = (state.department.users || []).filter(({ userId }) => userId !== payload)
  }
}

const actions = {
  async getOrganizations ({ commit, dispatch }) {
    commit('UPDATE_FORM_DISABLE_INDICATOR', true)
    await dispatch('getSelectedActiveProfile')
    axios
      .get(process.env.VUE_APP_INTERNAL_API_BASEURL + '/organization/', {
        params: {
          name: util.emptyToNull(state.filter.name),
          organizationNumbers: util.emptyToNull(state.filter.organizationNumber),
          isActive: state.filter.isActive,
          isSchool: (state.filter.school === 'School') ? true : null,
          isSchoolOwner: (state.filter.school === 'SchoolOwner') ? true : null,
          organizationSource: util.emptyToNull(state.filter.source),
          pageNumber: util.emptyToNull(state.paging.currentPage),
          pageSize: util.emptyToNull(state.paging.perPage)
        }
      })
      .then((response) => {
        commit('UPDATE_ORGANIZATIONS', response.data)
      }).finally(() => {
      commit('UPDATE_FORM_DISABLE_INDICATOR', false)
    })
  },
  resetFilter ({ commit }) {
    commit('UPDATE_FILTER', { ...default_filter })
    commit('UPDATE_PAGING', { ...default_paging })
    commit('UPDATE_SEARCH_FILTERS', false)
  },
  getOrganization ({ commit }, organizationNumber) {
    axios
      .get(process.env.VUE_APP_INTERNAL_API_BASEURL + '/organization/' + organizationNumber)
      .then(response => commit('UPDATE_ORGANIZATION', response.data))
  },
  getDepartment ({ commit }, departmentId) {
    axios
      .get(process.env.VUE_APP_INTERNAL_API_BASEURL + '/department/' + departmentId)
      .then(response => commit('UPDATE_DEPARTMENT', response.data))
  },
  getDepartments ({ commit }, organizationNumber) {
    axios
      .get(process.env.VUE_APP_INTERNAL_API_BASEURL + '/department/?organizationNumber=' + organizationNumber)
      .then(response => commit('UPDATE_DEPARTMENTS', response.data.response))
  },
  getDepartmentUsers ({ commit }, departmentId) {
    axios
      .get(process.env.VUE_APP_INTERNAL_API_BASEURL + '/department/' + departmentId + '/users')
      .then(response => commit('UPDATE_DEPARTMENT_USERS', response.data))
  },
  addDepartmentUser ({ commit }, { department, user }) {
    const userIds = (this.department?.users || []).map(({ userId }) => userId).concat(user.userId)
    axios
      .post(process.env.VUE_APP_INTERNAL_API_BASEURL + '/department/' + department.departmentId + '/users', userIds)
      .then(() => commit('ADD_DEPARTMENT_USER', { userId: user.userId, fullName: user.fullName, primaryRole: user.primaryRole }))
  },
  deleteDepartmentUser ({ commit }, { department, user }) {
    axios
      .delete(process.env.VUE_APP_INTERNAL_API_BASEURL + '/department/' + department.departmentId + '/users/' + user.userId)
      .then(() => commit('REMOVE_DEPARTMENT_USER', user.userId))
  },
  addDepartmentLicense ({ commit }, { departmentId, licenseId }) {
    axios
      .post(process.env.VUE_APP_INTERNAL_API_BASEURL + '/department/' + departmentId + '/licenses/' + licenseId)
  },
  deleteDepartmentLicense ({ commit }, { departmentId, licenseId }) {
    axios
      .delete(process.env.VUE_APP_INTERNAL_API_BASEURL + '/department/' + departmentId + '/licenses/' + licenseId)
  },
  resetOrganization ({ commit }) {
    commit('UPDATE_ORGANIZATION', { ...default_organization })
  },
  async create ({ commit }) {
    commit('UPDATE_CREATE_LOADING_INDICATOR', true)
    if (state.organization.numberOfPupilsOverride === 0 || state.organization.numberOfPupilsOverride === "0") {
      state.organization.numberOfPupilsOverride = null
    }
    await axios
      .put(process.env.VUE_APP_INTERNAL_API_BASEURL + '/organization/' + state.organization.organizationNumber , state.organization)
      .then((response) => {
        commit('UPDATE_ORGANIZATION', response.data)
      }).finally(() => {
        commit('UPDATE_CREATE_LOADING_INDICATOR', false)
      })
  },

  resetDepartment ({ commit }) {
    commit('UPDATE_DEPARTMENT', undefined)
  },

  async updateDepartment ({ commit }, department) {
    commit('UPDATE_CREATE_LOADING_INDICATOR', true)
    await axios
      .put(process.env.VUE_APP_INTERNAL_API_BASEURL + '/department/' + department.departmentId , department)
      .then((response) => {
        commit('UPDATE_DEPARTMENT', response.data)
      }).finally(() => {
        commit('UPDATE_CREATE_LOADING_INDICATOR', false)
      })
  },

  async createDepartment ({ commit }, department) {
    commit('UPDATE_CREATE_LOADING_INDICATOR', true)
    return await axios
      .post(process.env.VUE_APP_INTERNAL_API_BASEURL + '/department/', department)
      .then((response) => {
        commit('UPDATE_DEPARTMENT', response.data)
      }).finally(() => {
        commit('UPDATE_CREATE_LOADING_INDICATOR', false)
      })
  },

  async getOrganizationByOrganizationNumbers (state, { organizationNumbers }) {
    const params = new URLSearchParams()
    organizationNumbers.forEach(organizationNumber => params.append('organizationNumbers', organizationNumber))

    const response = await axios.get(process.env.VUE_APP_INTERNAL_API_BASEURL + '/organization/', {
      params: params
    })
    return response.data
  },
  getSelectedActiveProfile ({ commit, rootGetters }) {
    let isActiveProfileSet = rootGetters['sessionStorage/isActiveProfileSet']
    if(isActiveProfileSet) {
      let activeProfile = rootGetters['sessionStorage/activeProfile']
      if(activeProfile.activeProfileType === 'organization') {
        commit('UPDATE_ORGANIZATION_NUMBER', activeProfile.id)
        commit('UPDATE_SEARCH_FILTERS', true)
      }
    }
  }
}

const getters = {
  organization: state => state.organization,
  organizations: state => state.organizations,
  filter: state => state.filter,
  loading: state => state.loading,
  paging: state => state.paging,
  create: state => state.create,
  createLoading: state => state.createLoading,
  disableFilters: state => state.disableFilters,
  departments: state => state.organization.departments,
  department: state => state.department,
}

const organizationsModule = {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}

export default organizationsModule
